import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Box,
} from "@mui/material";
import { signInApi, resetPasswordApi } from "../api/auth";

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    const { error } = await signInApi(email, password);
    if (error) {
      setError(error.message);
    } else {
      navigate("/");
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Enter your email above first.");
      return;
    }
    const { error } = await resetPasswordApi(
      email,
      `${window.location.origin}/account`
    );
    if (error) setError(error.message);
    else setMessage("Password reset email sent.");
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        <Typography
          component={RouterLink}
          to="/"
          variant="h6"
          sx={{
            display: "block",
            textAlign: "center",
            mb: 5,
            color: "black",
            textDecoration: "none",
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
          }}
        >
          Lemanade
        </Typography>

        <Typography
          variant="subtitle2"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontWeight: 700,
            mb: 4,
          }}
        >
          Sign In
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }}>
            {message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d4d4d4" },
            }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d4d4d4" },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              bgcolor: "black",
              color: "white",
              py: 1.5,
              fontSize: "0.75rem",
              letterSpacing: "0.12em",
              mt: 1,
              "&:hover": { bgcolor: "#333" },
            }}
          >
            {loading ? "Please wait…" : "Sign In"}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Button
          onClick={handleForgotPassword}
          fullWidth
          sx={{
            color: "#737373",
            fontSize: "0.75rem",
            letterSpacing: "0.05em",
            textTransform: "none",
            "&:hover": { color: "black", bgcolor: "transparent" },
          }}
        >
          Forgot your password?
        </Button>

        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 3, color: "#737373" }}
        >
          Don&apos;t have an account?{" "}
          <Typography
            component={RouterLink}
            to="/signup"
            variant="body2"
            sx={{
              color: "black",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Create one
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
